import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cognitoConfig } from '../config/cognito';
import { signOut } from '../services/cognitoAuth';

/**
 * Logout Success Page
 *
 * This page is shown after logout is initiated.
 * It clears auth state and allows user to return to login.
 */
const Logout: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(true);

  useEffect(() => {
    const performLogout = async () => {
      // Clear all auth-related data
      console.log('🧹 Logout page: Cleaning up auth state');
      localStorage.removeItem('talrev_auth');
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      // Also clear any Cognito SDK session data
      const cognitoKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('CognitoIdentityServiceProvider.')
      );
      cognitoKeys.forEach(key => localStorage.removeItem(key));

      // Call Cognito SDK signOut to clear local session
      try {
        await signOut();
        console.log('✅ Local Cognito session cleared');
      } catch (err) {
        console.log('Cognito signOut completed');
      }

      console.log('✅ Auth state cleared - ready for logout');
      setIsLoggingOut(false);
    };

    performLogout();
  }, [navigate]);

  const handleReturnToLogin = () => {
    console.log('🔒 Redirecting to Cognito logout to clear server session...');

    // Redirect to Cognito logout endpoint which will clear server-side session cookies
    // and redirect back to the login page
    const domain = cognitoConfig.hostedUIDomain;
    const clientId = cognitoConfig.clientId;
    const logoutUri = encodeURIComponent(window.location.origin);
    const cognitoLogoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;

    window.location.href = cognitoLogoutUrl;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Logged Out Successfully</h2>
          <p className="text-gray-600 mb-6">
            You have been securely logged out of TalRev.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            You can close this tab or click the button below to sign in again.
          </p>
          <button
            onClick={handleReturnToLogin}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default Logout;
